import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell
} from 'recharts';

// ── Design tokens (tinyEmail-inspired light theme) ───────────
const C = {
  bg:       '#f5f6fa',
  surface:  '#ffffff',
  border:   '#e8eaef',
  border2:  '#d0d4e0',
  accent:   '#1a9e6e',   // tinyEmail green
  accent2:  '#3b82f6',
  warn:     '#f59e0b',
  danger:   '#ef4444',
  text:     '#0f1523',
  text2:    '#374151',
  muted:    '#6b7280',
  muted2:   '#9ca3af',
  nav:      '#0f1523',
  navText:  '#ffffff',
  pill:     '#f0faf5',
  pillText: '#1a9e6e',
  purple:   '#6366f1',
  orange:   '#ea580c',
};

const fmt     = n  => n == null ? '—' : Number(n).toLocaleString();
const pct     = n  => (!n || n === 0) ? '0.0%' : `${Number(n).toFixed(1)}%`;
const pctFull = n  => n == null ? '—' : `${Number(n).toFixed(2)}%`;
const fmtDate = d  => !d ? '—' : new Date(d).toLocaleDateString('en-US',
  { month:'short', day:'numeric', year:'numeric' });
const fmtShort = d => !d ? '—' : new Date(d).toLocaleDateString('en-US',
  { month:'short', day:'numeric' });
const ago = d => {
  if (!d) return '—';
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
  return `${Math.floor(diff/1440)}d ago`;
};

// ── Global CSS ───────────────────────────────────────────────
const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html, body { height:100%; background:${C.bg}; }
  body {
    color:${C.text};
    font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
    font-size:14px; line-height:1.5;
  }
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  ::-webkit-scrollbar { width:5px; height:5px; }
  ::-webkit-scrollbar-track { background:${C.bg}; }
  ::-webkit-scrollbar-thumb { background:${C.border2}; border-radius:3px; }
  button { cursor:pointer; border:none; background:none;
           color:inherit; font-family:inherit; font-size:inherit; }
  input, select { font-family:inherit; font-size:inherit; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:.4} }
  .fade-in { animation:fadeIn 0.25s ease both; }
  table { border-collapse:collapse; width:100%; }
  th, td { text-align:left; }
`;

function injectStyles() {
  if (document.getElementById('app-css')) return;
  const s = document.createElement('style');
  s.id = 'app-css';
  s.textContent = GLOBAL_CSS;
  document.head.appendChild(s);
}

// ── Base UI components ────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', justifyContent:'center',
      alignItems:'center', padding:60 }}>
      <div style={{ width:28, height:28,
        border:`2px solid ${C.border2}`,
        borderTopColor:C.accent, borderRadius:'50%',
        animation:'spin 0.7s linear infinite' }} />
    </div>
  );
}

function Card({ children, style={}, onClick, title, action }) {
  return (
    <div onClick={onClick} style={{
      background:C.surface, border:`1px solid ${C.border}`,
      borderRadius:10, overflow:'hidden',
      boxShadow:'0 1px 3px rgba(0,0,0,0.06)',
      cursor: onClick ? 'pointer' : 'default',
      transition:'box-shadow 0.2s, border-color 0.2s',
      ...style,
    }}
    onMouseEnter={e => onClick && (
      e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)',
      e.currentTarget.style.borderColor=C.accent+'60'
    )}
    onMouseLeave={e => onClick && (
      e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.06)',
      e.currentTarget.style.borderColor=C.border
    )}
    >
      {(title || action) && (
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', padding:'14px 18px',
          borderBottom:`1px solid ${C.border}` }}>
          <span style={{ fontWeight:600, fontSize:13,
            color:C.text }}>{title}</span>
          {action}
        </div>
      )}
      <div style={{ padding: title || action ? '16px 18px' : '18px' }}>
        {children}
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, color=C.text, trend, icon }) {
  return (
    <div style={{ padding:'18px 20px', background:C.surface,
      border:`1px solid ${C.border}`, borderRadius:10,
      boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', marginBottom:10 }}>
        <span style={{ fontSize:12, color:C.muted,
          fontWeight:500 }}>{label}</span>
        {icon && <span style={{ fontSize:18, opacity:0.5 }}>{icon}</span>}
      </div>
      <div style={{ fontSize:26, fontWeight:700, color,
        letterSpacing:'-0.02em', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.muted,
        marginTop:6 }}>{sub}</div>}
    </div>
  );
}

function Tag({ children, color=C.accent }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center',
      padding:'2px 10px', borderRadius:20, fontSize:11,
      fontWeight:600, background:color+'18', color }}>
      {children}
    </span>
  );
}

function RateBar({ value, max=100, color=C.accent, height=4 }) {
  const w = Math.min(Math.max(((value||0)/max)*100, 0), 100);
  return (
    <div style={{ height, background:C.border, borderRadius:3,
      overflow:'hidden', flex:1 }}>
      <div style={{ height:'100%', width:`${w}%`, background:color,
        borderRadius:3, transition:'width 0.5s ease' }} />
    </div>
  );
}

function DayPicker({ value, onChange, options=[7,14,30,60] }) {
  return (
    <div style={{ display:'flex', gap:4 }}>
      {options.map(d => (
        <button key={d} onClick={() => onChange(d)} style={{
          padding:'5px 12px', borderRadius:6, fontSize:12,
          fontWeight:500,
          border:`1px solid ${value===d ? C.accent : C.border2}`,
          background: value===d ? C.accent : C.surface,
          color: value===d ? '#fff' : C.muted,
          transition:'all 0.15s',
        }}>{d}d</button>
      ))}
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display:'flex', gap:0,
      borderBottom:`1px solid ${C.border}`, marginBottom:24 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding:'10px 18px', fontSize:13, fontWeight:500,
          color: active===t.id ? C.accent : C.muted,
          borderBottom: active===t.id
            ? `2px solid ${C.accent}` : '2px solid transparent',
          marginBottom:-1, transition:'all 0.15s',
          background:'none',
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// ── Top Navigation ────────────────────────────────────────────
function TopNav({ brands, activeBrand, setActiveBrand, page, setPage }) {
  const [open, setOpen] = useState(false);

  const PAGES = [
    { id:'dashboard',    label:'Dashboard' },
    { id:'campaigns',    label:'Campaigns' },
    { id:'analytics',    label:'Analytics' },
    { id:'segments',     label:'Segments'  },
    { id:'deliverability', label:'Deliverability' },
  ];

  const brand = brands.find(b => b.brand_id === activeBrand);

  return (
    <div style={{ background:C.nav, height:54, display:'flex',
      alignItems:'center', paddingLeft:24, paddingRight:24,
      position:'sticky', top:0, zIndex:100,
      boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }}>

      {/* Logo */}
      <div style={{ fontWeight:700, fontSize:16, color:'#fff',
        letterSpacing:'-0.02em', marginRight:40,
        display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:26, height:26, background:C.accent,
          borderRadius:6, display:'flex', alignItems:'center',
          justifyContent:'center', fontSize:14 }}>✉</div>
        MailOps
      </div>

      {/* Nav links */}
      <div style={{ display:'flex', gap:4, flex:1 }}>
        {PAGES.map(p => (
          <button key={p.id} onClick={() => setPage(p.id)} style={{
            padding:'6px 14px', borderRadius:6, fontSize:13,
            fontWeight:500,
            background: page===p.id ? 'rgba(255,255,255,0.12)' : 'transparent',
            color: page===p.id ? '#fff' : 'rgba(255,255,255,0.55)',
            transition:'all 0.15s',
          }}>{p.label}</button>
        ))}
      </div>

      {/* Brand selector */}
      <div style={{ position:'relative' }}>
        <button onClick={() => setOpen(!open)} style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'7px 14px', borderRadius:8,
          background:'rgba(255,255,255,0.1)',
          border:'1px solid rgba(255,255,255,0.15)',
          color:'#fff', fontSize:13, fontWeight:500,
          transition:'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.18)'}
        onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}
        >
          <span style={{ width:8, height:8, borderRadius:'50%',
            background:C.accent, flexShrink:0,
            animation:'pulse 2s ease infinite' }} />
          {brand?.name || 'Select Brand'}
          <span style={{ opacity:0.6, fontSize:10 }}>▼</span>
        </button>

        {open && (
          <div style={{ position:'absolute', right:0, top:'calc(100% + 8px)',
            background:C.surface, border:`1px solid ${C.border}`,
            borderRadius:10, boxShadow:'0 8px 30px rgba(0,0,0,0.15)',
            minWidth:220, zIndex:200, overflow:'hidden' }}>
            <div style={{ padding:'10px 14px 8px',
              borderBottom:`1px solid ${C.border}`,
              fontSize:11, color:C.muted, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'0.06em' }}>
              Select Brand
            </div>
            {brands.map(b => (
              <button key={b.brand_id}
                onClick={() => { setActiveBrand(b.brand_id); setOpen(false); }}
                style={{
                  width:'100%', display:'flex', alignItems:'center',
                  justifyContent:'space-between',
                  padding:'10px 14px', textAlign:'left',
                  background: b.brand_id===activeBrand
                    ? C.accent+'12' : 'transparent',
                  color: b.brand_id===activeBrand ? C.accent : C.text,
                  fontSize:13, transition:'background 0.1s',
                }}
                onMouseEnter={e => {
                  if (b.brand_id!==activeBrand)
                    e.currentTarget.style.background=C.bg;
                }}
                onMouseLeave={e => {
                  if (b.brand_id!==activeBrand)
                    e.currentTarget.style.background='transparent';
                }}
              >
                <span style={{ fontWeight: b.brand_id===activeBrand ? 600 : 400 }}>
                  {b.name}
                </span>
                {b.sent_today > 0 && (
                  <Tag color={C.accent}>{fmt(b.sent_today)} today</Tag>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dashboard page ────────────────────────────────────────────
function DashboardPage({ brand, setPage }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays]     = useState(30);

  useEffect(() => {
    if (!brand) return;
    setLoading(true);
    Promise.all([
      api.brandCampaigns(brand.brand_id, days),
      api.brandTrend(brand.brand_id, days),
    ]).then(([c, t]) => {
      setData({ campaigns: c.campaigns||[], trend:(t||[]).reverse() });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [brand, days]);

  if (!brand) return <NoBrand />;

  const recent = data?.campaigns?.slice(0,5) || [];
  const trend  = data?.trend || [];
  const totalSent = data?.campaigns?.reduce((s,c) => s+(c.sent_count||0), 0) || 0;
  const avgOpen   = data?.campaigns?.length
    ? data.campaigns.reduce((s,c) => s+(c.open_rate||0),0)/data.campaigns.length : 0;
  const avgClick  = data?.campaigns?.length
    ? data.campaigns.reduce((s,c) => s+(c.click_rate||0),0)/data.campaigns.length : 0;

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700,
            letterSpacing:'-0.02em' }}>{brand.name}</h1>
          <p style={{ color:C.muted, fontSize:13, marginTop:2 }}>
            Last synced {ago(brand.synced_at)} ·
            {fmt(brand.total_campaigns)} campaigns total
          </p>
        </div>
        <DayPicker value={days} onChange={setDays}
          options={[7,14,30,60]} />
      </div>

      {loading ? <Spinner /> : (
        <>
          <div style={{ display:'grid',
            gridTemplateColumns:'repeat(4,1fr)',
            gap:14, marginBottom:22 }}>
            <StatBox label="Sent Today"
              value={fmt(brand.sent_today||0)}
              color={C.text} icon="✉"
              sub="emails delivered" />
            <StatBox label="Avg Open Rate"
              value={pct(avgOpen)}
              color={C.accent} icon="◎"
              sub={`over ${days} days`} />
            <StatBox label="Avg Click Rate"
              value={pct(avgClick)}
              color={C.accent2} icon="↗"
              sub={`over ${days} days`} />
            <StatBox label="Total Sent"
              value={fmt(totalSent)}
              color={C.text} icon="◈"
              sub={`over ${days} days`} />
          </div>

          <div style={{ display:'grid',
            gridTemplateColumns:'1.6fr 1fr',
            gap:18, marginBottom:18 }}>

            <Card title="Open & Click Rate Trend">
              {trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={trend} margin={{ left:-10, right:10 }}>
                    <defs>
                      <linearGradient id="gO" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.accent} stopOpacity={0.15}/>
                        <stop offset="100%" stopColor={C.accent} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.accent2} stopOpacity={0.15}/>
                        <stop offset="100%" stopColor={C.accent2} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3"
                      stroke={C.border} vertical={false} />
                    <XAxis dataKey="date"
                      tick={{ fill:C.muted, fontSize:11 }}
                      tickFormatter={v => v?.slice(5)||''}
                      axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill:C.muted, fontSize:11 }}
                      tickFormatter={v=>`${v}%`}
                      axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background:C.surface,
                        border:`1px solid ${C.border}`,
                        borderRadius:8, fontSize:12,
                        boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(v,n) => [`${Number(v).toFixed(1)}%`, n]} />
                    <Area type="monotone" dataKey="avg_open_rate"
                      name="Open Rate" stroke={C.accent}
                      strokeWidth={2} fill="url(#gO)" dot={false} />
                    <Area type="monotone" dataKey="avg_click_rate"
                      name="Click Rate" stroke={C.accent2}
                      strokeWidth={2} fill="url(#gC)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </Card>

            <Card title="Recent Activity"
              action={
                <button onClick={() => setPage('campaigns')}
                  style={{ fontSize:12, color:C.accent,
                    fontWeight:500 }}>View all →</button>
              }>
              {recent.map((c, i) => (
                <div key={c.campaign_id} style={{
                  padding:'10px 0',
                  borderBottom: i<recent.length-1
                    ? `1px solid ${C.border}` : 'none',
                }}>
                  <div style={{ fontSize:12, fontWeight:500,
                    marginBottom:4, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap',
                    color:C.text }}>
                    {c.subject||c.name}
                  </div>
                  <div style={{ display:'flex',
                    alignItems:'center', gap:12 }}>
                    <span style={{ fontSize:11, color:C.muted }}>
                      {fmt(c.sent_count)} sent
                    </span>
                    <span style={{ fontSize:11,
                      color:C.accent, fontWeight:600 }}>
                      {pct(c.open_rate)} open
                    </span>
                    <span style={{ fontSize:11, color:C.muted,
                      marginLeft:'auto' }}>
                      {fmtShort(c.sent_at)}
                    </span>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          <Card title="Campaign Performance">
            <CampaignTable campaigns={data?.campaigns||[]}
              setPage={setPage} />
          </Card>
        </>
      )}
    </div>
  );
}

// ── Campaigns page ────────────────────────────────────────────
function CampaignsPage({ brand, setPage }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [days, setDays]           = useState(30);
  const [q, setQ]                 = useState('');

  useEffect(() => {
    if (!brand) return;
    setLoading(true);
    api.brandCampaigns(brand.brand_id, days)
      .then(d => { setCampaigns(d.campaigns||[]); setLoading(false); })
      .catch(() => setLoading(false));
  }, [brand, days]);

  if (!brand) return <NoBrand />;

  const filtered = campaigns.filter(c =>
    !q || (c.subject||c.name||'').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'center', marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:700,
          letterSpacing:'-0.02em' }}>Campaigns</h1>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search campaigns..."
            style={{ padding:'7px 14px', borderRadius:8,
              border:`1px solid ${C.border2}`, background:C.surface,
              color:C.text, width:220, outline:'none',
              transition:'border-color 0.2s' }}
            onFocus={e => e.target.style.borderColor=C.accent}
            onBlur={e => e.target.style.borderColor=C.border2}
          />
          <DayPicker value={days} onChange={setDays} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <Card>
          <CampaignTable campaigns={filtered} setPage={setPage} />
        </Card>
      )}
    </div>
  );
}

// ── Analytics page ────────────────────────────────────────────
function AnalyticsPage({ brand }) {
  const [tab, setTab]     = useState('delivery');
  const [days, setDays]   = useState(30);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!brand) return;
    setLoading(true);
    Promise.all([
      api.brandCampaigns(brand.brand_id, days),
      api.brandTrend(brand.brand_id, days),
    ]).then(([c, t]) => {
      setData({ campaigns:c.campaigns||[], trend:(t||[]).reverse() });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [brand, days]);

  if (!brand) return <NoBrand />;

  const camps   = data?.campaigns || [];
  const trend   = data?.trend     || [];
  const totalSent = camps.reduce((s,c) => s+(c.sent_count||0), 0);
  const avgOpen   = camps.length ? camps.reduce((s,c) => s+(c.open_rate||0),0)/camps.length : 0;
  const avgClick  = camps.length ? camps.reduce((s,c) => s+(c.click_rate||0),0)/camps.length : 0;
  const avgBounce = camps.length ? camps.reduce((s,c) => s+(c.bounce_rate||0),0)/camps.length : 0;

  const TABS = [
    { id:'delivery',    label:'Delivery' },
    { id:'engagement',  label:'Engagement' },
    { id:'performance', label:'Performance' },
  ];

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'center', marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:700,
          letterSpacing:'-0.02em' }}>Analytics</h1>
        <DayPicker value={days} onChange={setDays}
          options={[7,14,30,60,90]} />
      </div>

      <div style={{ display:'grid',
        gridTemplateColumns:'repeat(5,1fr)',
        gap:12, marginBottom:24 }}>
        {[
          { label:'Total Sent',  v:fmt(totalSent),   color:C.text },
          { label:'Avg Open',    v:pct(avgOpen),     color:C.accent },
          { label:'Avg Click',   v:pct(avgClick),    color:C.accent2 },
          { label:'Avg Bounce',  v:pct(avgBounce),
            color: avgBounce>2 ? C.danger : C.warn },
          { label:'Campaigns',   v:fmt(camps.length),color:C.text },
        ].map(s => (
          <StatBox key={s.label} label={s.label}
            value={s.v} color={s.color} />
        ))}
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {loading ? <Spinner /> : (
        <>
          {tab === 'delivery' && (
            <div style={{ display:'grid',
              gridTemplateColumns:'1fr 1fr', gap:18 }}>
              <Card title="Deliverability Trend">
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={trend} margin={{ left:-10, right:10 }}>
                      <defs>
                        <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.accent} stopOpacity={0.15}/>
                          <stop offset="100%" stopColor={C.accent} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={C.border} vertical={false} />
                      <XAxis dataKey="date"
                        tick={{ fill:C.muted, fontSize:11 }}
                        tickFormatter={v => v?.slice(5)||''}
                        axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill:C.muted, fontSize:11 }}
                        axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background:C.surface,
                        border:`1px solid ${C.border}`,
                        borderRadius:8, fontSize:12 }}
                        formatter={v => [fmt(v), 'Sent']} />
                      <Area type="monotone" dataKey="total_sent"
                        name="Sent" stroke={C.accent}
                        strokeWidth={2} fill="url(#gT)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </Card>

              <Card title="Bounce Rate by Campaign">
                {camps.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={camps.slice(0,15).map(c => ({
                        name: (c.subject||c.name||'').slice(0,20),
                        bounce: c.bounce_rate||0,
                      }))}
                      margin={{ left:-10, right:10 }}>
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={C.border} vertical={false} />
                      <XAxis dataKey="name"
                        tick={{ fill:C.muted, fontSize:10 }}
                        axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill:C.muted, fontSize:11 }}
                        tickFormatter={v=>`${v}%`}
                        axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background:C.surface,
                        border:`1px solid ${C.border}`,
                        borderRadius:8, fontSize:12 }}
                        formatter={v=>[`${Number(v).toFixed(2)}%`,'Bounce']} />
                      <Bar dataKey="bounce" name="Bounce Rate"
                        fill={C.warn} radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </Card>
            </div>
          )}

          {tab === 'engagement' && (
            <div style={{ display:'grid',
              gridTemplateColumns:'1fr 1fr', gap:18 }}>
              <Card title="Open Rate Over Time">
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trend} margin={{ left:-10, right:10 }}>
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={C.border} vertical={false} />
                      <XAxis dataKey="date"
                        tick={{ fill:C.muted, fontSize:11 }}
                        tickFormatter={v => v?.slice(5)||''}
                        axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill:C.muted, fontSize:11 }}
                        tickFormatter={v=>`${v}%`}
                        axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background:C.surface,
                        border:`1px solid ${C.border}`,
                        borderRadius:8, fontSize:12 }}
                        formatter={(v,n) => [`${Number(v).toFixed(1)}%`,n]} />
                      <Line type="monotone" dataKey="avg_open_rate"
                        name="Open Rate" stroke={C.accent}
                        strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="avg_click_rate"
                        name="Click Rate" stroke={C.accent2}
                        strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </Card>

              <Card title="Top Campaigns by Open Rate">
                <div>
                  {camps
                    .filter(c => c.open_rate > 0)
                    .sort((a,b) => b.open_rate - a.open_rate)
                    .slice(0,8)
                    .map((c,i) => (
                    <div key={c.campaign_id} style={{
                      padding:'9px 0',
                      borderBottom: i<7 ? `1px solid ${C.border}` : 'none',
                    }}>
                      <div style={{ display:'flex',
                        justifyContent:'space-between',
                        marginBottom:6 }}>
                        <span style={{ fontSize:12, color:C.text,
                          fontWeight:500, maxWidth:260,
                          overflow:'hidden', textOverflow:'ellipsis',
                          whiteSpace:'nowrap' }}>
                          {c.subject||c.name}
                        </span>
                        <span style={{ fontSize:12, color:C.accent,
                          fontWeight:700, flexShrink:0, marginLeft:8 }}>
                          {pct(c.open_rate)}
                        </span>
                      </div>
                      <RateBar value={c.open_rate} />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {tab === 'performance' && (
            <Card title="Full Campaign Performance">
              <CampaignTable campaigns={camps} setPage={() => {}} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── Campaign detail page ──────────────────────────────────────
function CampaignDetailPage({ campaignId, setPage }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.campaign(campaignId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [campaignId]);

  if (loading) return <Spinner />;
  if (!data?.campaign) return (
    <div style={{ padding:40, color:C.muted }}>Campaign not found.</div>
  );

  const { campaign:c, hourly, top_links, domains, top_openers } = data;
  const hasEvents = (hourly||[]).length > 0;
  const hourlyChart = (hourly||[]).map(h => ({
    ...h,
    time: new Date(h.hour_bucket).toLocaleTimeString('en-US',
      { hour:'2-digit', minute:'2-digit', hour12:true }),
  }));

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <button onClick={() => window.history.back?.() || setPage('campaigns')}
        style={{ display:'flex', alignItems:'center', gap:6,
          color:C.muted, fontSize:13, marginBottom:20,
          fontWeight:500 }}
        onMouseEnter={e => e.currentTarget.style.color=C.accent}
        onMouseLeave={e => e.currentTarget.style.color=C.muted}
      >← Back to Campaigns</button>

      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center',
          gap:10, marginBottom:8 }}>
          <Tag color={c.status==='complete' ? C.accent : C.warn}>
            {c.status}
          </Tag>
          <span style={{ fontSize:13, color:C.muted }}>
            {c.brand_name}
          </span>
        </div>
        <h1 style={{ fontSize:22, fontWeight:700,
          letterSpacing:'-0.02em', marginBottom:6 }}>
          {c.subject||c.name}
        </h1>
        <div style={{ fontSize:13, color:C.muted }}>
          Sent {fmtDate(c.sent_at)}
        </div>

        <div style={{ display:'flex', flexWrap:'wrap',
          gap:8, marginTop:14, alignItems:'center' }}>
          {(c.included_lists||[]).map(l => (
            <div key={l.list_id} title="Included list" style={{
              display:'inline-flex', alignItems:'center', gap:5,
              padding:'4px 12px', borderRadius:20,
              background: C.accent+'12',
              border:`1px solid ${C.accent}30`,
              fontSize:12, fontWeight:500, color:C.accent,
            }}>
              <span style={{ fontSize:9 }}>LIST</span>
              {l.name}
              {l.is_all && <span style={{ fontSize:10,
                opacity:0.6 }}>(All)</span>}
            </div>
          ))}
          {c.segment_name && (
            <div title="Segment filter applied" style={{
              display:'inline-flex', alignItems:'center', gap:5,
              padding:'4px 12px', borderRadius:20,
              background:'#8b5cf620',
              border:'1px solid #8b5cf640',
              fontSize:12, fontWeight:500, color:'#8b5cf6',
            }}>
              <span style={{ fontSize:9 }}>SEGMENT</span>
              {c.segment_name}
            </div>
          )}
          {(c.excluded_lists||[]).map(l => (
            <div key={l.list_id} title="Excluded list" style={{
              display:'inline-flex', alignItems:'center', gap:5,
              padding:'4px 12px', borderRadius:20,
              background: C.danger+'10',
              border:`1px solid ${C.danger}30`,
              fontSize:12, fontWeight:500, color:C.danger,
            }}>
              <span style={{ fontSize:9 }}>EXCL</span>
              {l.name}
            </div>
          ))}
          {(!c.included_lists?.length && !c.segment_name && !c.excluded_lists?.length) && (
            <span style={{ fontSize:12, color:C.muted }}>
              No list/segment data — re-run poller
            </span>
          )}
        </div>
      </div>

      <div style={{ display:'grid',
        gridTemplateColumns:'repeat(6,1fr)',
        gap:12, marginBottom:22 }}>
        {[
          { label:'Sent',      v:fmt(c.sent_count),          color:C.text },
          { label:'Open Rate', v:pct(c.open_rate),           color:C.accent },
          { label:'Click Rate',v:pct(c.click_rate),          color:C.accent2 },
          { label:'CTOR',      v:pct(c.ctor),                color:'#8b5cf6' },
          { label:'Bounce',    v:pct(c.bounce_rate),
            color: c.bounce_rate>2 ? C.danger : C.warn },
          { label:'Complaint', v:pctFull(c.complaint_rate),
            color: c.complaint_rate>0.08 ? C.danger : C.muted },
        ].map(m => (
          <StatBox key={m.label} label={m.label}
            value={m.v} color={m.color} />
        ))}
      </div>

      <div style={{ display:'grid',
        gridTemplateColumns:'1fr 1fr',
        gap:18, marginBottom:18 }}>
        <Card title="Engagement Over Time">
          {hasEvents ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={hourlyChart} margin={{ left:-10, right:10 }}>
                <defs>
                  <linearGradient id="hO" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.accent} stopOpacity={0.2}/>
                    <stop offset="100%" stopColor={C.accent} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="hC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.accent2} stopOpacity={0.2}/>
                    <stop offset="100%" stopColor={C.accent2} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3"
                  stroke={C.border} vertical={false} />
                <XAxis dataKey="time"
                  tick={{ fill:C.muted, fontSize:10 }}
                  axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:C.muted, fontSize:10 }}
                  axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background:C.surface,
                  border:`1px solid ${C.border}`,
                  borderRadius:8, fontSize:12 }} />
                <Area type="monotone" dataKey="opens"
                  name="Opens" stroke={C.accent}
                  strokeWidth={2} fill="url(#hO)" dot={false} />
                <Area type="monotone" dataKey="clicks"
                  name="Clicks" stroke={C.accent2}
                  strokeWidth={2} fill="url(#hC)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:200, display:'flex',
              flexDirection:'column', alignItems:'center',
              justifyContent:'center', color:C.muted, gap:8 }}>
              <span style={{ fontSize:28 }}>📭</span>
              <span style={{ fontSize:13 }}>
                No events yet — connect BigMailer webhooks
              </span>
            </div>
          )}
        </Card>

        <Card title="Top Openers">
          {top_openers?.length > 0 ? (
            <table>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                  <th style={{ padding:'6px 0', fontSize:11,
                    color:C.muted, fontWeight:500 }}>Email</th>
                  <th style={{ padding:'6px 0', fontSize:11,
                    color:C.muted, fontWeight:500,
                    textAlign:'right' }}>Opens</th>
                </tr>
              </thead>
              <tbody>
                {top_openers.map((o,i) => (
                  <tr key={o.contact_email}
                    style={{ borderBottom: i<top_openers.length-1
                      ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding:'9px 0', fontSize:13 }}>
                      {o.contact_email}
                    </td>
                    <td style={{ padding:'9px 0',
                      textAlign:'right' }}>
                      <Tag color={C.accent}>{o.open_count}×</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState msg="No opener data yet" />
          )}
        </Card>
      </div>

      <div style={{ display:'grid',
        gridTemplateColumns:'1fr 1fr', gap:18 }}>
        <Card title="Top Clicked Links">
          {top_links?.length > 0 ? (
            <div>
              {top_links.map((l,i) => (
                <div key={i} style={{ padding:'10px 0',
                  borderBottom: i<top_links.length-1
                    ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display:'flex',
                    justifyContent:'space-between',
                    alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:12, color:C.accent2,
                      overflow:'hidden', textOverflow:'ellipsis',
                      whiteSpace:'nowrap', flex:1,
                      marginRight:12 }}>{l.click_url}</span>
                    <Tag color={C.accent2}>{fmt(l.clicks)}</Tag>
                  </div>
                  <RateBar value={l.clicks}
                    max={top_links[0]?.clicks||1}
                    color={C.accent2} height={3} />
                </div>
              ))}
            </div>
          ) : <EmptyState msg="No click data yet" />}
        </Card>

        <Card title="Domain Breakdown">
          {domains?.length > 0 ? (
            <table>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                  {['Domain','Opens','Clicks','Bounces'].map(h => (
                    <th key={h} style={{ padding:'6px 8px',
                      fontSize:11, color:C.muted,
                      fontWeight:500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {domains.map((d,i) => (
                  <tr key={d.email_domain} style={{
                    borderBottom: i<domains.length-1
                      ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding:'8px',
                      fontSize:13, fontWeight:500 }}>
                      {d.email_domain}
                    </td>
                    <td style={{ padding:'8px',
                      fontSize:13, color:C.accent }}>
                      {fmt(d.opens)}
                    </td>
                    <td style={{ padding:'8px',
                      fontSize:13, color:C.accent2 }}>
                      {fmt(d.clicks)}
                    </td>
                    <td style={{ padding:'8px', fontSize:13,
                      color: d.bounces>0 ? C.danger : C.muted }}>
                      {fmt(d.bounces)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState msg="No domain data yet" />}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SEGMENT BUILDER PAGE
// ══════════════════════════════════════════════════════════════

const _RAW_BASE = process.env.REACT_APP_API_URL || '';
const API_BASE = _RAW_BASE.replace(/\/api$/, '');
const IS_ADMIN = true; // Set to false to hide the universal toggle

const CONDITION_SCHEMA = {
  Activity: {
    filters: ['Clicked','Emailed','Not Clicked','Not Emailed','Not Opened','Opened'],
    operators: ['After','At Least','At Least Once','Before','Between','In the last','Never','Not after','Not before','Not between','Not in the last'],
    valueType: 'number_days',
  },
  Campaign: { filters: ['In','Not in'], operators: null, valueType: 'text' },
  Customer: {
    filters: ['Country','Created at','Domain','Email','First name','Internet Service Provider','Last name','Last order date','Last order price','Orders count','Phone','Postal code','State','Tags'],
    operators: ['Contains','Does not contain','Equals','Does not equal','Is blank','Is not blank','Starts with','Ends with','Greater than','Less than'],
    valueType: 'text',
  },
  'Custom Attributes': { filters: [], operators: ['Contains','Does not contain','Equals','Does not equal','Is blank','Is not blank'], valueType: 'text' },
  'Link URL': { filters: ['Clicked','Not Clicked'], operators: ['Contains','Equals','Starts with'], valueType: 'text' },
  Sender: { filters: ['In','Not in'], operators: null, valueType: 'text' },
  'Traffic Control': { filters: ['% of Subscribers','% of Subscribers between'], operators: null, valueType: 'percent' },
  Workflow: { filters: ['In','Not in'], operators: null, valueType: 'text' },
};

const AUDIENCE_OPTIONS = ['All Customers','All Subscribers','Active Subscribers'];
const segUid = () => Math.random().toString(36).slice(2, 8);
const newCond  = () => ({ id: segUid(), category: 'Activity', filter: 'Opened', operator: 'At Least', value: '', value2: '' });
const newGroup = () => ({ id: segUid(), conditions: [newCond()] });

function SegSel({ value, onChange, options, placeholder, disabled, width }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      style={{ height:34, padding:'0 10px', borderRadius:6,
        border:`1px solid ${C.border2}`, background: disabled ? C.bg : '#fff',
        fontSize:13, color: C.text, cursor: disabled ? 'not-allowed' : 'pointer',
        outline:'none', minWidth: width||120, opacity: disabled ? 0.6 : 1 }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function SegInp({ value, onChange, placeholder='', type='text', disabled, width }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled}
      style={{ height:34, padding:'0 10px', borderRadius:6,
        border:`1px solid ${C.border2}`, background: disabled ? C.bg : '#fff',
        fontSize:13, color: C.text, outline:'none',
        width: width||100, opacity: disabled ? 0.6 : 1 }} />
  );
}

function SegToggle({ checked, onChange, label }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
      <div onClick={() => onChange(!checked)}
        style={{ width:38, height:20, borderRadius:99,
          background: checked ? C.purple : C.border2,
          position:'relative', transition:'background 0.2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:2, left: checked ? 19 : 2,
          width:16, height:16, borderRadius:99,
          background:'#fff', transition:'left 0.2s' }} />
      </div>
      <span style={{ fontSize:13, color: C.text2, fontWeight: checked ? 500 : 400 }}>{label}</span>
    </label>
  );
}

function SegConditionRow({ condition, onChange, onRemove, disabled }) {
  const schema = CONDITION_SCHEMA[condition.category] || {};
  const up = (k, v) => onChange({ ...condition, [k]: v });

  const valueInputs = () => {
    const vt = schema.valueType;
    if (vt === 'number_days') {
      const two = condition.operator === 'Between' || condition.operator === 'Not between';
      const noVal = condition.operator === 'Never' || condition.operator === 'At Least Once';
      return (
        <>
          {!noVal && <SegInp disabled={disabled} value={condition.value} onChange={v => up('value',v)} placeholder="Count" type="number" width={70} />}
          {two && <SegInp disabled={disabled} value={condition.value2} onChange={v => up('value2',v)} placeholder="and" type="number" width={70} />}
          {!noVal && <span style={{ fontSize:12, color:C.muted, alignSelf:'center' }}>days</span>}
        </>
      );
    }
    if (vt === 'percent') return (
      <>
        <SegInp disabled={disabled} value={condition.value} onChange={v => up('value',v)} placeholder="%" type="number" width={70} />
        {condition.filter === '% of Subscribers between' && <SegInp disabled={disabled} value={condition.value2} onChange={v => up('value2',v)} placeholder="and %" type="number" width={70} />}
      </>
    );
    if (condition.operator === 'Is blank' || condition.operator === 'Is not blank') return null;
    return <SegInp disabled={disabled} value={condition.value} onChange={v => up('value',v)} placeholder="Value" width={160} />;
  };

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', padding:'8px 0' }}>
      <SegSel disabled={disabled} value={condition.category}
        onChange={v => onChange({ ...condition, category:v, filter:CONDITION_SCHEMA[v]?.filters?.[0]||'', operator:CONDITION_SCHEMA[v]?.operators?.[0]||'', value:'', value2:'' })}
        options={Object.keys(CONDITION_SCHEMA)} width={150} />
      {schema.filters?.length > 0 && <SegSel disabled={disabled} value={condition.filter} onChange={v => up('filter',v)} options={schema.filters} width={140} />}
      {schema.operators?.length > 0 && <SegSel disabled={disabled} value={condition.operator} onChange={v => up('operator',v)} options={schema.operators} width={130} />}
      {valueInputs()}
      {!disabled && (
        <button onClick={onRemove}
          style={{ width:26, height:26, borderRadius:6, border:`1px solid ${C.border}`,
            background:'none', color:C.muted2, fontSize:16, flexShrink:0 }}>×</button>
      )}
    </div>
  );
}

function SegConditionGroup({ group, groupIndex, isLast, onChange, onRemoveGroup, disabled }) {
  const addCond    = () => onChange({ ...group, conditions:[...group.conditions, newCond()] });
  const updateCond = (i,u) => { const c=[...group.conditions]; c[i]=u; onChange({ ...group, conditions:c }); };
  const removeCond = (i)   => onChange({ ...group, conditions:group.conditions.filter((_,j)=>j!==i) });

  return (
    <div>
      <div style={{ background: disabled ? C.bg : C.surface, border:`1px solid ${C.border}`,
        borderRadius:10, padding:'14px 18px', position:'relative' }}>
        {disabled && (
          <div style={{ position:'absolute', top:10, right:14, fontSize:11, color:C.muted2 }}>🔒 read-only</div>
        )}
        {group.conditions.map((cond,i) => (
          <div key={cond.id}>
            <SegConditionRow condition={cond} onChange={u=>updateCond(i,u)} onRemove={()=>removeCond(i)} disabled={disabled} />
            {i < group.conditions.length-1 && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'2px 0' }}>
                <div style={{ flex:1, height:1, background:C.border }} />
                <span style={{ background:'#e0e7ff', color:'#4f46e5', padding:'2px 10px', borderRadius:99, fontSize:11, fontWeight:600 }}>AND</span>
                <div style={{ flex:1, height:1, background:C.border }} />
              </div>
            )}
          </div>
        ))}
        {!disabled && (
          <button onClick={addCond}
            style={{ marginTop:10, color:C.purple, fontSize:13, fontWeight:500,
              display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ fontSize:16 }}>+</span> Add condition
          </button>
        )}
        {!disabled && groupIndex > 0 && (
          <button onClick={onRemoveGroup}
            style={{ position:'absolute', top:10, right:12, color:C.border2, fontSize:20 }}>×</button>
        )}
      </div>
      {!isLast && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0' }}>
          <div style={{ flex:1, height:1, background:C.border }} />
          <span style={{ background:'#fef3c7', color:'#d97706', padding:'3px 12px', borderRadius:99, fontSize:11, fontWeight:600 }}>OR</span>
          <div style={{ flex:1, height:1, background:C.border }} />
        </div>
      )}
    </div>
  );
}

function SegSummary({ summary, loading, onRefresh, brandName }) {
  return (
    <div style={{ width:240, flexShrink:0 }}>
      <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:16, position:'sticky', top:74 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Segment Summary</span>
          <button onClick={onRefresh} disabled={loading}
            style={{ fontSize:12, color:C.purple, fontWeight:500 }}>{loading ? '…' : '↻ Refresh'}</button>
        </div>
        {brandName && <div style={{ fontSize:11, color:C.muted, marginBottom:10 }}>Counting: <strong style={{ color:C.purple }}>{brandName}</strong></div>}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:26, fontWeight:700, color: loading ? C.muted2 : C.text }}>
            {loading ? '—' : (summary?.totalContacts??0).toLocaleString()}
          </div>
          <div style={{ fontSize:12, color:C.muted }}>total customers</div>
          <div style={{ fontSize:12, color:C.muted }}>{loading ? '—' : (summary?.totalSubscribers??0).toLocaleString()} subscribers</div>
        </div>
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.text2, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Applied filters</div>
          <div style={{ fontSize:12, color:C.muted, lineHeight:1.7 }}>{summary?.filterSummary||'No conditions yet'}</div>
        </div>
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.text2, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.05em' }}>Breakdown</div>
          {[
            { label:'opened', value:summary?.openedPct??0, color:C.accent },
            { label:'clicked', value:summary?.clickedPct??0, color:C.accent2 },
            { label:'unsubscribed', value:summary?.unsubPct??0, color:C.warn },
          ].map(({ label,value,color }) => (
            <div key={label} style={{ marginBottom:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:C.muted, marginBottom:3 }}>
                <span>{Math.round(value)}% {label}</span>
              </div>
              <div style={{ height:4, background:C.border, borderRadius:99 }}>
                <div style={{ width:`${Math.min(value,100)}%`, height:'100%', background:color, borderRadius:99, transition:'width 0.4s' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SegBrandPicker({ brands, onSelect }) {
  const [search, setSearch] = useState('');
  const filtered = brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:'-0.02em' }}>Segments</h1>
        <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>Select a brand to manage its audience segments</p>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brands…"
        style={{ height:36, padding:'0 14px', borderRadius:8, border:`1px solid ${C.border2}`,
          background:C.surface, color:C.text, width:260, outline:'none', marginBottom:20 }} />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))', gap:12 }}>
        {filtered.map(brand => (
          <div key={brand.brand_id} onClick={() => onSelect(brand)}
            style={{ padding:'18px 20px', borderRadius:10, cursor:'pointer',
              border:`1px solid ${C.border}`, background:C.surface,
              boxShadow:'0 1px 3px rgba(0,0,0,0.05)', transition:'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=C.purple; e.currentTarget.style.background='#faf5ff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background=C.surface; }}
          >
            <div style={{ width:36, height:36, borderRadius:8, background:'#ede9fe',
              display:'flex', alignItems:'center', justifyContent:'center', marginBottom:10 }}>
              <span style={{ fontSize:16, fontWeight:700, color:C.purple }}>{brand.name.charAt(0)}</span>
            </div>
            <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:3 }}>{brand.name}</div>
            <div style={{ fontSize:12, color:C.muted }}>{(brand.segment_count??0)} segments · {(brand.total_contacts??0).toLocaleString()} contacts</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SegList({ brand, segments, universalSegments, onNew, onEdit, onDelete, onClone, onBack, onRefreshCount }) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('brand');

  const shown = tab === 'brand'
    ? segments.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : universalSegments.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  const tabBtn = (id, label, count, countColor) => (
    <button onClick={() => setTab(id)} style={{
      padding:'10px 18px', fontSize:13, fontWeight: tab===id ? 600 : 400,
      color: tab===id ? C.purple : C.muted,
      borderBottom: tab===id ? `2px solid ${C.purple}` : '2px solid transparent',
      marginBottom:-1, background:'none',
    }}>
      {label}{' '}
      <span style={{ background: countColor+'22', color: countColor,
        padding:'1px 8px', borderRadius:99, fontSize:11, fontWeight:600, marginLeft:4 }}>{count}</span>
    </button>
  );

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button onClick={onBack}
          style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`,
            fontSize:18, color:C.muted }}>←</button>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:'-0.02em' }}>{brand.name}</h1>
          <p style={{ color:C.muted, fontSize:13, marginTop:2 }}>Brand-specific and universal segments</p>
        </div>
        <button onClick={onNew}
          style={{ padding:'8px 18px', background:C.purple, color:'#fff',
            border:'none', borderRadius:8, fontSize:13, fontWeight:500 }}>
          + New Segment
        </button>
      </div>

      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, marginBottom:16 }}>
        {tabBtn('brand', 'Brand Segments', segments.length, C.purple)}
        {tabBtn('universal', 'Universal Segments', universalSegments.length, C.orange)}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
        style={{ height:36, padding:'0 14px', borderRadius:8, border:`1px solid ${C.border2}`,
          background:C.surface, color:C.text, width:260, outline:'none', marginBottom:16 }} />

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
        {shown.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:C.muted, fontSize:14 }}>
            {tab === 'brand'
              ? <span>No segments yet. <button onClick={onNew} style={{ color:C.purple }}>Create one?</button></span>
              : 'No universal segments yet.'}
          </div>
        ) : shown.map((seg,i) => (
          <div key={seg.segment_id||seg.id} style={{ display:'flex', alignItems:'center', padding:'14px 20px',
            borderBottom: i<shown.length-1 ? `1px solid ${C.border}` : 'none', gap:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:C.text }}>{seg.name}</span>
                {seg.is_universal && <span style={{ background:'#fff7ed', color:C.orange, padding:'1px 8px', borderRadius:99, fontSize:11, fontWeight:600 }}>🌐 Universal</span>}
                {seg.cloned_from && <span style={{ background:C.bg, color:C.muted, padding:'1px 8px', borderRadius:99, fontSize:11 }}>Cloned</span>}
              </div>
              <div style={{ fontSize:12, color:C.muted }}>
                Updated {seg.updated_at ? new Date(seg.updated_at).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'2-digit'}) : '—'}
              </div>
            </div>
            <div style={{ textAlign:'right', minWidth:90 }}>
              <div style={{ fontSize:16, fontWeight:600, color: seg._refreshing ? C.muted2 : C.text }}>
                {seg._refreshing ? '…'
                  : seg.contact_count != null
                    ? seg.contact_count.toLocaleString()
                    : (seg.brand_total_contacts??0).toLocaleString()}
              </div>
              <div style={{ fontSize:12, color:C.muted }}>
                {seg._refreshing ? 'refreshing…'
                  : seg.contact_count != null ? 'Matched' : 'Brand total'}
              </div>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              {tab === 'universal' ? (
                <button onClick={() => onClone(seg)}
                  style={{ padding:'5px 12px', borderRadius:6,
                    border:`1px solid ${C.purple}40`, color:C.purple, fontSize:12 }}>
                  Clone to brand
                </button>
              ) : (
                <>
                  <button onClick={() => onEdit(seg)}
                    style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, fontSize:12 }}>Edit</button>
                  <button
                    onClick={() => onRefreshCount && onRefreshCount(seg)}
                    title="Refresh count"
                    style={{ width:28, height:28, borderRadius:6,
                      border:`1px solid ${C.border}`, background:'none',
                      color:C.muted, fontSize:15, display:'flex',
                      alignItems:'center', justifyContent:'center',
                      cursor:'pointer', lineHeight:1 }}>↻</button>
                  <button onClick={() => onDelete(seg.segment_id||seg.id)}
                    style={{ padding:'5px 10px', borderRadius:6, border:`1px solid #fee2e2`, color:C.danger, fontSize:12 }}>×</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SegEditor({ segment, brand, onSave, onCancel }) {
  const isUniversal = segment?.is_universal || false;
  const readonly = isUniversal && !IS_ADMIN;

  const [name, setName]             = useState(segment?.name || '');
  const [audience, setAudience]     = useState(segment?.audience || 'All Customers');
  const [audienceMode, setAudienceMode] = useState(segment?.audience_mode || 'In');
  const [groups, setGroups]         = useState(segment?.groups || [newGroup()]);
  const [universal, setUniversal]   = useState(isUniversal);
  const [summary, setSummary]       = useState(null);
  const [sumLoading, setSumLoading] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [editName, setEditName]     = useState(!segment?.id);

  const addGroup    = () => setGroups(g => [...g, newGroup()]);
  const updateGroup = (i,u) => setGroups(g => { const n=[...g]; n[i]=u; return n; });
  const removeGroup = (i)   => setGroups(g => g.filter((_,j) => j!==i));

  // Auto-refresh summary when editor opens with existing segment
  useEffect(() => {
    if (segment?.segment_id && groups.length > 0) {
      refreshSummary();
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    setSumLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/segments/preview`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ brand_id:brand.brand_id, audience, audience_mode:audienceMode, groups }),
      });
      if (res.ok) { const d = await res.json(); setSummary(d.data || d); }
    } catch {
      setSummary({
        totalContacts: Math.floor(Math.random()*40000+1000),
        totalSubscribers: Math.floor(Math.random()*35000+500),
        openedPct: Math.random()*75+5,
        clickedPct: Math.random()*40+2,
        unsubPct: Math.random()*4,
        filterSummary: groups.flatMap(g=>g.conditions.map(c=>`${c.category} ${c.filter} ${c.operator} ${c.value||''}`)).join(' OR '),
      });
    } finally { setSumLoading(false); }
  }, [brand.brand_id, audience, audienceMode, groups]);

  const handleSave = async () => {
    if (!name.trim()) { alert('Please enter a segment name'); return; }
    setSaving(true);
    try {
      const payload = { name, brand_id:brand.brand_id, audience, audience_mode:audienceMode, groups, is_universal: IS_ADMIN ? universal : false };
      const url = segment?.segment_id ? `${API_BASE}/api/segments/${segment.segment_id}` : `${API_BASE}/api/segments`;
      const method = segment?.segment_id ? 'PUT' : 'POST';
      const headers = { 'Content-Type':'application/json' };
      if (IS_ADMIN) headers['x-is-admin'] = 'true';
      const res = await fetch(url, { method, headers, body:JSON.stringify(payload) });
      if (res.ok) { const d = await res.json(); onSave(Array.isArray(d) ? d : (d.data || d)); }
      else { alert('Save failed — check backend'); }
    } catch {
      onSave({ segment_id:segment?.segment_id||segUid(), name, brand_id:brand.brand_id, audience, groups, is_universal: IS_ADMIN?universal:false, updated_at:new Date().toISOString(), contact_count:0 });
    } finally { setSaving(false); }
  };

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1280 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={onCancel}
            style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:18, color:C.muted }}>←</button>
          {!readonly && editName ? (
            <input autoFocus value={name} onChange={e=>setName(e.target.value)} onBlur={() => name && setEditName(false)}
              placeholder="Segment name…"
              style={{ fontSize:20, fontWeight:600, border:'none', borderBottom:`2px solid ${C.purple}`,
                outline:'none', color:C.text, background:'transparent', minWidth:260 }} />
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <h1 style={{ fontSize:20, fontWeight:700, color:C.text }}>{name||'Untitled'}</h1>
              {!readonly && <button onClick={()=>setEditName(true)} style={{ color:C.muted2, fontSize:14 }}>✎</button>}
            </div>
          )}
          <span style={{ background:'#ede9fe', color:C.purple, padding:'2px 10px', borderRadius:99, fontSize:12, fontWeight:600 }}>{brand.name}</span>
          {(universal||isUniversal) && <span style={{ background:'#fff7ed', color:C.orange, padding:'2px 10px', borderRadius:99, fontSize:12, fontWeight:600 }}>🌐 Universal</span>}
          {readonly && <span style={{ background:C.bg, color:C.muted, padding:'2px 10px', borderRadius:99, fontSize:12 }}>🔒 Read-only</span>}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel}
            style={{ padding:'7px 16px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, color:C.text2 }}>Cancel</button>
          {!readonly && (
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'7px 18px', borderRadius:8, background:C.purple, color:'#fff',
                border:'none', fontSize:13, fontWeight:500, opacity:saving?0.7:1 }}>
              {saving ? 'Saving…' : 'Save and Exit'}
            </button>
          )}
        </div>
      </div>

      <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>
        <div style={{ flex:1, minWidth:0 }}>

          {IS_ADMIN && (
            <div style={{ background: universal ? '#fff7ed' : C.surface,
              border:`1px solid ${universal ? '#fed7aa' : C.border}`,
              borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
              <SegToggle checked={universal} onChange={setUniversal}
                label="Universal segment — available to all brands (each brand sees its own contacts)" />
              {universal && (
                <p style={{ marginTop:10, fontSize:12, color:C.orange }}>
                  ⚠️ This will appear in every brand's segment list as read-only. They can clone it to customise locally.
                </p>
              )}
            </div>
          )}

          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.text2, marginBottom:12 }}>Audience</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <SegSel disabled={readonly} value={audienceMode} onChange={setAudienceMode} options={['In','Not in']} width={100} />
              <SegSel disabled={readonly} value={audience} onChange={setAudience} options={AUDIENCE_OPTIONS} width={180} />
            </div>
          </div>

          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.text2, marginBottom:12 }}>Conditions</div>
            {groups.map((g,i) => (
              <SegConditionGroup key={g.id} group={g} groupIndex={i} isLast={i===groups.length-1}
                onChange={u=>updateGroup(i,u)} onRemoveGroup={()=>removeGroup(i)} disabled={readonly} />
            ))}
            {!readonly && (
              <button onClick={addGroup}
                style={{ marginTop:12, width:'100%', padding:'8px', borderRadius:8,
                  border:`1px dashed ${C.border2}`, color:C.purple, fontSize:13,
                  fontWeight:500, background:'none' }}>
                + Add another set of conditions
              </button>
            )}
          </div>
        </div>

        <SegSummary summary={summary} loading={sumLoading} onRefresh={refreshSummary} brandName={brand.name} />
      </div>
    </div>
  );
}

function SegmentsPage({ brands }) {
  const [segView, setSegView]           = useState('brands'); // brands | list | editor
  const [activeBrand, setActiveBrand]   = useState(null);
  const [segments, setSegments]         = useState([]);
  const [universalSegs, setUniversalSegs] = useState([]);
  const [editingSeg, setEditingSeg]     = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/segments?universal=true`)
      .then(r => r.json())
      .then(d => setUniversalSegs(Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : [])))
      .catch(() => {});
  }, []);

  const selectBrand = async (brand) => {
    setActiveBrand(brand);
    setSegView('list');
    try {
      const res = await fetch(`${API_BASE}/api/segments?brand_id=${brand.brand_id}`);
      const d = await res.json();
      const segs = Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : []);
      setSegments(segs);
      // Auto-refresh counts for all segments in background
      refreshAllCounts(segs, brand);
    } catch { setSegments([]); }
  };

  const refreshAllCounts = async (segs, brand) => {
    // Refresh counts one by one to avoid hammering the DB
    for (const seg of segs) {
      if (!seg.groups || !seg.groups.length) continue; // skip segments with no conditions
      try {
        const res = await fetch(`${API_BASE}/api/segments/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_id: brand.brand_id, groups: seg.groups }),
        });
        if (res.ok) {
          const d = await res.json();
          const data = d.data || d;
          const count = data.totalContacts || 0;
          // Update count in UI
          setSegments(prev => prev.map(s =>
            s.segment_id === seg.segment_id
              ? { ...s, contact_count: count }
              : s
          ));
          // Persist count to DB
          fetch(`${API_BASE}/api/segments/${seg.segment_id}/count`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ count }),
          }).catch(() => {});
        }
      } catch {}
      // Small delay between requests
      await new Promise(r => setTimeout(r, 100));
    }
  };

  const handleSave = (saved) => {
    if (saved.is_universal) {
      setUniversalSegs(prev => {
        const idx = prev.findIndex(s=>s.id===saved.id);
        if (idx>=0) { const n=[...prev]; n[idx]=saved; return n; }
        return [saved,...prev];
      });
    } else {
      setSegments(prev => {
        const idx = prev.findIndex(s=>s.id===saved.id);
        if (idx>=0) { const n=[...prev]; n[idx]=saved; return n; }
        return [saved,...prev];
      });
    }
    setSegView('list');
  };

  const handleClone = async (seg) => {
    const clone = { ...seg, segment_id:undefined, name:`${seg.name} (${activeBrand.name})`,
      is_universal:false, brand_id:activeBrand.brand_id, cloned_from:seg.segment_id||seg.id };
    try {
      const res = await fetch(`${API_BASE}/api/segments`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify(clone),
      });
      if (res.ok) { const d = await res.json(); handleSave(Array.isArray(d) ? d : (d.data || d)); return; }
    } catch {}
    handleSave({ ...clone, id:segUid(), updated_at:new Date().toISOString(), contact_count:0 });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this segment?')) return;
    try { await fetch(`${API_BASE}/api/segments/${id}`, { method:'DELETE' }); } catch {}
    setSegments(prev => prev.filter(s=>s.segment_id!==id));
  };

  const handleRefreshCount = async (seg) => {
    // Show loading
    setSegments(prev => prev.map(s =>
      s.segment_id === seg.segment_id ? { ...s, _refreshing: true } : s
    ));
    try {
      const res = await fetch(`${API_BASE}/api/segments/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand_id: activeBrand.brand_id,
          groups: seg.groups || [],
        }),
      });
      if (res.ok) {
        const d = await res.json();
        const data = d.data || d;
        setSegments(prev => prev.map(s =>
          s.segment_id === seg.segment_id
            ? { ...s, contact_count: data.totalContacts, _refreshing: false }
            : s
        ));
      } else {
        setSegments(prev => prev.map(s =>
          s.segment_id === seg.segment_id ? { ...s, _refreshing: false } : s
        ));
      }
    } catch {
      setSegments(prev => prev.map(s =>
        s.segment_id === seg.segment_id ? { ...s, _refreshing: false } : s
      ));
    }
  };

  if (segView === 'brands') return <SegBrandPicker brands={brands} onSelect={selectBrand} />;
  if (segView === 'list') return (
    <SegList brand={activeBrand} segments={segments} universalSegments={universalSegs}
      onNew={() => { setEditingSeg(null); setSegView('editor'); }}
      onEdit={s => { setEditingSeg(s); setSegView('editor'); }}
      onDelete={handleDelete} onClone={handleClone}
      onRefreshCount={handleRefreshCount}
      onBack={() => setSegView('brands')} />
  );
  return (
    <SegEditor segment={editingSeg} brand={activeBrand}
      onSave={handleSave} onCancel={() => setSegView('list')} />
  );
}

// ── Shared components ─────────────────────────────────────────
function CampaignTable({ campaigns, setPage }) {
  if (!campaigns.length) return <EmptyState msg="No campaigns found." />;
  return (
    <table>
      <thead>
        <tr style={{ borderBottom:`1px solid ${C.border}` }}>
          {['Subject','Sent','Open Rate','Click Rate','CTOR','Bounce','Date'].map(h => (
            <th key={h} style={{ padding:'8px 10px', fontSize:11, color:C.muted,
              fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em',
              whiteSpace:'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c, i) => (
          <tr key={c.campaign_id}
            onClick={() => setPage(`campaign:${c.campaign_id}`)}
            style={{ borderBottom:`1px solid ${C.border}`, cursor:'pointer', transition:'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background=C.bg}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}
          >
            <td style={{ padding:'11px 10px', maxWidth:280 }}>
              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                fontSize:13, fontWeight:500, color:C.text }}>{c.subject||c.name||'—'}</div>
            </td>
            <td style={{ padding:'11px 10px', fontSize:12, color:C.muted, whiteSpace:'nowrap' }}>{fmt(c.sent_count)}</td>
            <td style={{ padding:'11px 10px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <RateBar value={c.open_rate||0} />
                <span style={{ fontSize:12, color:C.accent, fontWeight:600, minWidth:40 }}>{pct(c.open_rate)}</span>
              </div>
            </td>
            <td style={{ padding:'11px 10px', fontSize:12, color:C.accent2, fontWeight:600 }}>{pct(c.click_rate)}</td>
            <td style={{ padding:'11px 10px', fontSize:12, color:C.muted }}>{pct(c.ctor)}</td>
            <td style={{ padding:'11px 10px', fontSize:12, color: c.bounce_rate>2 ? C.danger : C.muted }}>{pct(c.bounce_rate)}</td>
            <td style={{ padding:'11px 10px', fontSize:12, color:C.muted, whiteSpace:'nowrap' }}>{fmtShort(c.sent_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyChart() {
  return (
    <div style={{ height:200, display:'flex', alignItems:'center',
      justifyContent:'center', color:C.muted, fontSize:13 }}>No data available</div>
  );
}

function EmptyState({ msg }) {
  return (
    <div style={{ padding:'24px 0', color:C.muted, fontSize:13, textAlign:'center' }}>{msg}</div>
  );
}

function NoBrand() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:'60vh', flexDirection:'column', gap:12, color:C.muted }}>
      <span style={{ fontSize:36 }}>↗</span>
      <span style={{ fontSize:14 }}>Select a brand from the top-right dropdown</span>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────
export default function App() {
  injectStyles();
  const [brands, setBrands]           = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [page, setPage]               = useState('dashboard');
  const [campaignId, setCampaignId]   = useState(null);

  useEffect(() => {
    api.brands().then(b => {
      setBrands(b||[]);
      if (b?.length) setActiveBrand(b[0].brand_id);
    }).catch(() => {});
  }, []);

  const brand = brands.find(b => b.brand_id===activeBrand)||null;

  const handlePage = useCallback(p => {
    if (p.startsWith('campaign:')) {
      setCampaignId(p.replace('campaign:',''));
      setPage('campaign-detail');
    } else {
      setPage(p);
    }
  }, []);

  let content;
  if (page==='campaign-detail' && campaignId) {
    content = <CampaignDetailPage campaignId={campaignId} setPage={handlePage} />;
  } else if (page==='campaigns') {
    content = <CampaignsPage brand={brand} setPage={handlePage} />;
  } else if (page==='analytics') {
    content = <AnalyticsPage brand={brand} />;
  } else if (page==='segments') {
    content = <SegmentsPage brands={brands} />;
  } else if (page==='deliverability') {
    content = <ISPDashboard brands={brands} activeBrand={brands.find(b => b.brand_id===activeBrand)||brands[0]} />;
  } else {
    content = <DashboardPage brand={brand} setPage={handlePage} />;
  }

  return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      <TopNav brands={brands} activeBrand={activeBrand}
        setActiveBrand={id => { setActiveBrand(id); setPage('dashboard'); }}
        page={page} setPage={handlePage} />
      <div style={{ maxWidth:1440, margin:'0 auto' }}>
        {content}
      </div>
    </div>
  );
}
